import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'

export interface Community {
  id: string
  name: string
  description: string
  type: 'geographic' | 'professional' | 'cultural' | 'interest' | 'support'
  region?: string
  language?: string
  culturalFocus?: string[]
  location?: {
    city: string
    state: string
    country: string
  }
  memberCount: number
  isPrivate: boolean
  joinApprovalRequired: boolean
  adminIds: string[]
  moderatorIds: string[]
  rules?: string[]
  allowsEvents: boolean
  allowsBusinessPostings: boolean
  allowsHousingPostings: boolean
  coverImage?: string
  createdAt: string
  updatedAt: string
  status: 'active' | 'archived' | 'suspended'
}

export interface Event {
  id: string
  organizerId: string
  communityId?: string
  title: string
  description: string
  category: 'cultural' | 'religious' | 'professional' | 'social' | 'educational' | 'business' | 'sports' | 'music_arts' | 'food' | 'family' | 'fundraising' | 'other'
  type: 'in_person' | 'virtual' | 'hybrid'
  culturalSignificance?: string
  traditionalElements?: string[]
  dresscode?: string
  languageOfEvent?: string[]
  startDateTime: string
  endDateTime: string
  timeZone: string
  venue?: {
    name: string
    address: string
    city: string
    state: string
    country: string
    coordinates?: { lat: number; lng: number }
  }
  virtualLink?: string
  isFree: boolean
  ticketPrice?: number
  currency?: string
  capacity?: number
  registrationRequired: boolean
  registrationDeadline?: string
  featuredImage?: string
  additionalImages?: string[]
  allowsComments: boolean
  attendeeCount: number
  interestedCount: number
  createdAt: string
  updatedAt: string
  status: 'draft' | 'active' | 'flagged' | 'suspended' | 'canceled' | 'archived'
}

export interface CommunityPost {
  id: string
  authorId: string
  communityId: string
  content: string
  images?: string[]
  type: 'text' | 'image' | 'event_share' | 'business_recommendation' | 'housing_tip'
  tags?: string[]
  mentions?: string[]
  likesCount: number
  commentsCount: number
  sharesCount: number
  isLiked: boolean
  isPinned: boolean
  createdAt: string
  updatedAt: string
  status: 'active' | 'hidden' | 'deleted'
}

interface CommunityState {
  communities: Community[]
  myCommunities: Community[]
  recommendedCommunities: Community[]
  currentCommunity: Community | null
  events: Event[]
  upcomingEvents: Event[]
  myEvents: Event[]
  currentEvent: Event | null
  posts: CommunityPost[]
  communityPosts: CommunityPost[]
  loading: boolean
  eventsLoading: boolean
  postsLoading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

const initialState: CommunityState = {
  communities: [],
  myCommunities: [],
  recommendedCommunities: [],
  currentCommunity: null,
  events: [],
  upcomingEvents: [],
  myEvents: [],
  currentEvent: null,
  posts: [],
  communityPosts: [],
  loading: false,
  eventsLoading: false,
  postsLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
}

const API_BASE = '/api/backend'
const COMMUNITY_FEATURE_ENABLED = process.env.NEXT_PUBLIC_ENABLE_COMMUNITIES === 'true'

// Async thunks
export const fetchCommunities = createAsyncThunk(
  'community/fetchCommunities',
  async ({ page = 1, limit = 20, filters = {} }: {
    page?: number
    limit?: number
    filters?: Record<string, any>
  }, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...Object.entries(filters).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = Array.isArray(value) ? value.join(',') : value.toString()
          }
          return acc
        }, {} as Record<string, string>)
      })

      const response = await axios.get(`${API_BASE}/communities?${params}`)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch communities')
    }
  }
)

export const fetchMyCommunities = createAsyncThunk(
  'community/fetchMyCommunities',
  async (_, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      const response = await axios.get(`${API_BASE}/communities/my`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch my communities')
    }
  }
)

export const fetchCommunityById = createAsyncThunk(
  'community/fetchCommunityById',
  async (id: string, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      const response = await axios.get(`${API_BASE}/communities/${id}`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch community')
    }
  }
)

export const joinCommunity = createAsyncThunk(
  'community/joinCommunity',
  async (communityId: string, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      const response = await axios.post(`${API_BASE}/communities/${communityId}/join`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to join community')
    }
  }
)

export const leaveCommunity = createAsyncThunk(
  'community/leaveCommunity',
  async (communityId: string, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      await axios.delete(`${API_BASE}/communities/${communityId}/leave`)
      return communityId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to leave community')
    }
  }
)

export const fetchEvents = createAsyncThunk(
  'community/fetchEvents',
  async ({ page = 1, limit = 20, filters = {} }: {
    page?: number
    limit?: number
    filters?: Record<string, any>
  }, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...Object.entries(filters).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = Array.isArray(value) ? value.join(',') : value.toString()
          }
          return acc
        }, {} as Record<string, string>)
      })

      const response = await axios.get(`${API_BASE}/events?${params}`)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch events')
    }
  }
)

export const fetchUpcomingEvents = createAsyncThunk(
  'community/fetchUpcomingEvents',
  async (_, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      const response = await axios.get(`${API_BASE}/events?upcoming=true&limit=10`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch upcoming events')
    }
  }
)

export const fetchEventById = createAsyncThunk(
  'community/fetchEventById',
  async (id: string, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      const response = await axios.get(`${API_BASE}/events/${id}`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch event')
    }
  }
)

export const attendEvent = createAsyncThunk(
  'community/attendEvent',
  async (eventId: string, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      const response = await axios.post(`${API_BASE}/events/${eventId}/attend`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to RSVP to event')
    }
  }
)

export const fetchCommunityPosts = createAsyncThunk(
  'community/fetchCommunityPosts',
  async ({ communityId, page = 1, limit = 20 }: {
    communityId: string
    page?: number
    limit?: number
  }, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      const response = await axios.get(`${API_BASE}/communities/${communityId}/posts?page=${page}&limit=${limit}`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch community posts')
    }
  }
)

export const createCommunityPost = createAsyncThunk(
  'community/createPost',
  async ({ communityId, postData }: {
    communityId: string
    postData: Omit<CommunityPost, 'id' | 'createdAt' | 'updatedAt' | 'likesCount' | 'commentsCount' | 'sharesCount' | 'isLiked' | 'isPinned'>
  }, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      const response = await axios.post(`${API_BASE}/communities/${communityId}/posts`, postData)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create post')
    }
  }
)

export const likePost = createAsyncThunk(
  'community/likePost',
  async (postId: string, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      const response = await axios.post(`${API_BASE}/posts/${postId}/like`)
      return { postId, liked: true }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to like post')
    }
  }
)

export const unlikePost = createAsyncThunk(
  'community/unlikePost',
  async (postId: string, { rejectWithValue }) => {
    if (!COMMUNITY_FEATURE_ENABLED) {
      return rejectWithValue('Community features are disabled')
    }
    try {
      await axios.delete(`${API_BASE}/posts/${postId}/like`)
      return { postId, liked: false }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to unlike post')
    }
  }
)

const communitySlice = createSlice({
  name: 'community',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentCommunity: (state, action: PayloadAction<Community | null>) => {
      state.currentCommunity = action.payload
    },
    setCurrentEvent: (state, action: PayloadAction<Event | null>) => {
      state.currentEvent = action.payload
    },
    clearCommunityPosts: (state) => {
      state.communityPosts = []
    },
  },
  extraReducers: (builder) => {
    // fetchCommunities
    builder
      .addCase(fetchCommunities.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCommunities.fulfilled, (state, action) => {
        state.loading = false
        state.communities = action.payload.data
        state.pagination = action.payload.meta.pagination
      })
      .addCase(fetchCommunities.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // fetchMyCommunities
    builder
      .addCase(fetchMyCommunities.fulfilled, (state, action) => {
        state.myCommunities = action.payload
      })

    // fetchCommunityById
    builder
      .addCase(fetchCommunityById.fulfilled, (state, action) => {
        state.currentCommunity = action.payload
      })

    // joinCommunity
    builder
      .addCase(joinCommunity.fulfilled, (state, action) => {
        state.myCommunities.push(action.payload)
        if (state.currentCommunity && state.currentCommunity.id === action.payload.id) {
          state.currentCommunity.memberCount += 1
        }
      })

    // leaveCommunity
    builder
      .addCase(leaveCommunity.fulfilled, (state, action) => {
        state.myCommunities = state.myCommunities.filter(c => c.id !== action.payload)
        if (state.currentCommunity && state.currentCommunity.id === action.payload) {
          state.currentCommunity.memberCount -= 1
        }
      })

    // fetchEvents
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.eventsLoading = true
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.eventsLoading = false
        state.events = action.payload.data
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.eventsLoading = false
        state.error = action.payload as string
      })

    // fetchUpcomingEvents
    builder
      .addCase(fetchUpcomingEvents.fulfilled, (state, action) => {
        state.upcomingEvents = action.payload
      })

    // fetchEventById
    builder
      .addCase(fetchEventById.fulfilled, (state, action) => {
        state.currentEvent = action.payload
      })

    // attendEvent
    builder
      .addCase(attendEvent.fulfilled, (state, action) => {
        if (state.currentEvent && state.currentEvent.id === action.payload.eventId) {
          state.currentEvent.attendeeCount += 1
        }
      })

    // fetchCommunityPosts
    builder
      .addCase(fetchCommunityPosts.pending, (state) => {
        state.postsLoading = true
      })
      .addCase(fetchCommunityPosts.fulfilled, (state, action) => {
        state.postsLoading = false
        state.communityPosts = action.payload
      })
      .addCase(fetchCommunityPosts.rejected, (state, action) => {
        state.postsLoading = false
        state.error = action.payload as string
      })

    // createCommunityPost
    builder
      .addCase(createCommunityPost.fulfilled, (state, action) => {
        state.communityPosts.unshift(action.payload)
      })

    // likePost and unlikePost
    builder
      .addCase(likePost.fulfilled, (state, action) => {
        const post = state.communityPosts.find(p => p.id === action.payload.postId)
        if (post) {
          post.isLiked = true
          post.likesCount += 1
        }
      })
      .addCase(unlikePost.fulfilled, (state, action) => {
        const post = state.communityPosts.find(p => p.id === action.payload.postId)
        if (post) {
          post.isLiked = false
          post.likesCount -= 1
        }
      })
  },
})

export const { 
  clearError, 
  setCurrentCommunity, 
  setCurrentEvent, 
  clearCommunityPosts 
} = communitySlice.actions

export default communitySlice.reducer
